import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import useError from 'hooks/useError';
import useSmartlook from 'hooks/useSmartlook';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import { logger } from '../logger';
import oAuthClient, { AuthError } from './core';

export function useAuth() {
  const { showError } = useError();
  const { error } = useSmartlook();
  const queryClient = useQueryClient();
  const { reset } = useEventCollector();
  const { t } = useTranslation('error');

  const loginWithKeycloakLink = useCallback(
    async (
      email: string,
      password: string,
      clientUUID: string,
      callbackUrl: string,
    ) => {
      try {
        const json = await oAuthClient.login({
          email,
          password,
          clientUUID,
          callbackUrl,
        });

        return json;
      } catch (err) {
        error('[useAuth][loginWithKeycloakLink]', err);
        logger.error(
          '[useAuth][loginWithKeycloakLink] failed',
          {
            clientUUID,
          },
          err as Error,
        );

        if (err instanceof AuthError) {
          if (err.error.type === 'no-access') {
            showError({
              message: t('unauthorized'),
              type: 'notification',
            });
          }
          if (err.error.type === 'unknown-error') {
            showError({
              message: t('generic'),
              type: 'notification',
            });
          }
        }
        return undefined;
      }
    },
    [showError, error, t],
  );

  const keycloakLogout = useCallback(
    (redirect: string) => {
      reset();
      sessionStorage.clear();
      queryClient.clear();

      window.location.assign(oAuthClient.createLogoutUrl(redirect));
    },
    [reset, queryClient],
  );

  return {
    loginWithKeycloakLink,
    keycloakLogout,
  };
}
